(() => {
  'use strict';
  const partition = items => {
    const products = items.filter(item => typeof item.available === 'boolean');
    const ordered = products.filter(item => item.available).concat(products.filter(item => !item.available));
    let cursor = 0;
    return items.map(item => typeof item.available === 'boolean' ? ordered[cursor++] : item);
  };
  window.GbStockFirst = { partition };
  const nativeRuns = new WeakMap();
  const indexCache = new Map();
  const availability = new Map();
  const queued = [];
  let running = 0;
  let scheduled = false;
  const cardSelector = '.grid-product,.product,.product-card,.gb-col-product,.sb-product,.gb-dyad-cards__product,.mini-card';
  const groupSelector = '.products,.product-grid,.all-products,.catalogue,.sb-products,.gb-col-grid,.gb-dyad-cards__grid,.gb-dyad-cards__colours,.grid--uniform';
  const productHandle = card => {
    if (card.dataset.productHandle) return card.dataset.productHandle;
    const links = card.matches('a[href]') ? [card] : [...card.querySelectorAll('a[href]')];
    for (const link of links) {
      const match = new URL(link.href, location.origin).pathname.match(/\/products\/([^/]+)/);
      if (match) return decodeURIComponent(match[1]);
    }
    return null;
  };
  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    setTimeout(() => {
      scheduled = false;
      document.querySelectorAll('[data-gb-stock-list]').forEach(nativeList);
      const groups = new Set(document.querySelectorAll(groupSelector));
      document.querySelectorAll(cardSelector).forEach(card => { if (card.parentElement) groups.add(card.parentElement); });
      groups.forEach(editorialList);
    }, 60);
  };
  const drain = () => {
    while (running < 4 && queued.length) {
      const task = queued.shift();
      running++;
      task().finally(() => { running--; drain(); });
    }
  };
  const getAvailability = handle => {
    if (availability.has(handle)) return availability.get(handle);
    const promise = new Promise(resolve => queued.push(async () => {
      try {
        const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
        const response = await fetch(root + 'products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' });
        if (!response.ok) throw new Error('Product unavailable');
        const product = await response.json();
        resolve(typeof product.available === 'boolean' ? product.available : null);
      } catch {
        resolve(null);
      }
    }));
    availability.set(handle, promise);
    drain();
    return promise;
  };
  const editorialRuns = new WeakMap();
  async function editorialList(group) {
    if (group.id === 'pick-panel' || group.closest('[data-gb-stock-list]')) return;
    const cards = [...group.children].filter(card => card.matches(cardSelector) || (group.matches('.products,.product-grid,.all-products,.catalogue,.gb-dyad-cards__colours') && card.matches('a[href]') && productHandle(card)));
    if (cards.length < 2) return;
    const prior = editorialRuns.get(group);
    if (prior?.length === cards.length && prior.every((card, index) => card === cards[index])) return;
    editorialRuns.set(group, cards);
    const classified = await Promise.all(cards.map(async card => {
      if (card.dataset.gbAvailable === 'true' || card.dataset.gbAvailable === 'false') return { card, available: card.dataset.gbAvailable === 'true' };
      const handle = productHandle(card);
      return { card, available: handle ? await getAvailability(handle) : null };
    }));
    if (!group.isConnected || classified.some(item => item.available === null) || cards.some(card => card.parentElement !== group)) return;
    const ordered = partition(classified).map(item => item.card);
    ordered.forEach((card, index) => {
      const current = [...group.children].filter(child => cards.includes(child))[index];
      if (current !== card) group.insertBefore(card, current);
    });
    editorialRuns.set(group, ordered);
    group.dataset.gbStockReady = 'true';
  }
  const requestJson = async url => {
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) throw new Error('Stock index request failed');
    return response.json();
  };
  async function catalogue(source) {
    const url = new URL(source);
    url.hash = '';
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    url.searchParams.delete('sections');
    url.searchParams.set('view', 'gb-stock-index');
    const key = url.href;
    const cached = indexCache.get(key);
    if (cached && Date.now() - cached.created < 60000) return cached.promise;
    const promise = (async () => {
      url.searchParams.set('page', '1');
      const first = await requestJson(url);
      if (!Array.isArray(first.items) || !Number.isInteger(first.pages) || first.pages > 100) throw new Error('Invalid stock index');
      const pages = [first];
      for (let start = 2; start <= first.pages; start += 4) {
        const requests = [];
        for (let page = start; page <= Math.min(start + 3, first.pages); page++) {
          const next = new URL(url);
          next.searchParams.set('page', String(page));
          requests.push(requestJson(next));
        }
        pages.push(...await Promise.all(requests));
      }
      const items = pages.flatMap(page => page.items);
      if (items.length !== first.count || new Set(items.map(item => item.key)).size !== items.length) throw new Error('Incomplete stock index');
      return items;
    })();
    indexCache.set(key, { created: Date.now(), promise });
    if (indexCache.size > 8) indexCache.delete(indexCache.keys().next().value);
    promise.catch(() => indexCache.delete(key));
    return promise;
  }
  async function nativeList(wrapper) {
    if (nativeRuns.has(wrapper)) return;
    const source = location.href;
    const size = Number(wrapper.dataset.gbStockPageSize);
    const page = Number(wrapper.dataset.gbStockPage);
    const count = Number(wrapper.dataset.gbStockCount);
    const grid = wrapper.querySelector('[data-gb-stock-items]');
    if (!grid || !size || !page) return;
    nativeRuns.set(wrapper, source);
    if (count <= size) { wrapper.dataset.gbStockReady = 'true'; return; }
    wrapper.dataset.gbStockReady = 'loading';
    grid.setAttribute('aria-busy', 'true');
    try {
      const index = await catalogue(source);
      const ordered = partition(index);
      const wanted = ordered.slice((page - 1) * size, page * size);
      const cards = new Map([...grid.children].filter(card => card.dataset.gbStockKey).map(card => [card.dataset.gbStockKey, card]));
      const sourcePages = new Set(wanted.filter(item => !cards.has(item.key)).map(item => Math.floor(index.findIndex(row => row.key === item.key) / size) + 1));
      const pages = [...sourcePages];
      for (let start = 0; start < pages.length; start += 4) {
        await Promise.all(pages.slice(start, start + 4).map(async originalPage => {
          const url = new URL(source);
          url.hash = '';
          url.searchParams.delete('view');
          url.searchParams.delete('sections');
          url.searchParams.set('section_id', wrapper.dataset.gbStockSection);
          url.searchParams.set('page', String(originalPage));
          const response = await fetch(url, { credentials: 'same-origin' });
          if (!response.ok) throw new Error('Product cards request failed');
          const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
          const remoteGrid = parsed.querySelector('[data-gb-stock-items]');
          if (!remoteGrid) throw new Error('Missing product cards');
          [...remoteGrid.children].forEach(card => {
            if (card.dataset.gbStockKey) cards.set(card.dataset.gbStockKey, document.importNode(card, true));
          });
        }));
      }
      if (wanted.some(item => !cards.has(item.key))) throw new Error('Incomplete product cards');
      if (!wrapper.isConnected || location.href !== source) return;
      grid.replaceChildren(...wanted.map(item => cards.get(item.key)));
      wrapper.dataset.gbStockReady = 'true';
      window.theme?.reinitProductGridItem?.(grid);
      document.dispatchEvent(new CustomEvent('gb:stock-first-ready', { detail: { count: ordered.length, page, size } }));
    } catch (error) {
      if (wrapper.isConnected) wrapper.dataset.gbStockReady = 'failed';
      console.warn('Goosebump stock ordering:', error.message);
    } finally {
      grid.removeAttribute('aria-busy');
    }
  }
  function initialize() {
    schedule();
    new MutationObserver(schedule).observe(document.getElementById('MainContent') || document.body, { subtree: true, childList: true });
    document.addEventListener('collection:reloaded', schedule);
    document.addEventListener('shopify:section:load', schedule);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize);
  else initialize();
})();
